// NewsBodyEditor.js
// React 16.8.6 + Quill 1.3.x (images disabled)
// Comments in en_US
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Make Quill global for plugins that expect window.Quill (no plugins used here though)
if (typeof window !== 'undefined') {
    window.Quill = Quill;
}

// We will use Delta to strip any incoming image embeds on paste.
const Delta = Quill.import('delta');

export function NewsBodyEditor({
                                   initialHtml = '',
                                   onChange,
                                   readOnly = false,
                                   placeholder = 'Escreve aqui o corpo da notícia…',
                                   minLines = 15, // optional: control min editor height by lines
                               }) {
    const quillRef = useRef(null);

    /* ---------- Actions ---------- */
    const doUndo = useCallback(() => {
        const q = quillRef.current && quillRef.current.getEditor();
        if (q && q.history) q.history.undo();
    }, []);

    const doRedo = useCallback(() => {
        const q = quillRef.current && quillRef.current.getEditor();
        if (q && q.history) q.history.redo();
    }, []);

    /* ---------- Hard-disable images: paste/drag/img HTML ---------- */
    useEffect(() => {
        const q = quillRef.current && quillRef.current.getEditor();
        if (!q) return;

        // 1) Strip <img> elements when pasting HTML
        q.clipboard.addMatcher('IMG', () => new Delta());

        // 2) Extra safety: remove any image embeds produced by paste converters
        q.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
            if (!delta || !delta.ops) return delta;
            const cleaned = new Delta(
                delta.ops.filter(op => !(op && op.insert && typeof op.insert === 'object' && op.insert.image))
            );
            return cleaned;
        });

        // 3) Block clipboard images (files) pasted directly
        const onPaste = (e) => {
            const items = e.clipboardData && Array.from(e.clipboardData.items || []);
            if (items && items.some(i => i.kind === 'file' || (i.type && i.type.indexOf('image/') === 0))) {
                e.preventDefault();
            }
        };

        // 4) Block drag&drop of image files
        const onDrop = (e) => {
            const files = e.dataTransfer && Array.from(e.dataTransfer.files || []);
            if (files && files.some(f => f.type && f.type.indexOf('image/') === 0)) {
                e.preventDefault();
            }
        };

        q.root.addEventListener('paste', onPaste);
        q.root.addEventListener('drop', onDrop);

        return () => {
            q.root.removeEventListener('paste', onPaste);
            q.root.removeEventListener('drop', onDrop);
        };
    }, []);

    /* ---------- Stable modules (do not recreate per render) ---------- */
    // Use a ref proxy so toolbar handlers stay up-to-date without changing modules identity
    const handlersRef = useRef({ doUndo, doRedo });
    handlersRef.current.doUndo = doUndo;
    handlersRef.current.doRedo = doRedo;

    const modules = useMemo(
        () => ({
            toolbar: {
                container: '#news-toolbar',
                handlers: {
                    undo() { handlersRef.current.doUndo(); },
                    redo() { handlersRef.current.doRedo(); },
                    // no image handler
                },
            },
            history: { delay: 500, maxStack: 500, userOnly: true },
            clipboard: { matchVisual: false },
            keyboard: {}, // keep Quill defaults
            // no imageResize module
        }),
        [] // keep identity stable
    );

    const formats = useMemo(
        () => [
            // inline
            'bold', 'italic', 'underline', 'strike', 'link', 'background', 'color',
            // blocks
            'header', 'list', 'indent', 'align',
            // media: (intentionally no 'image')
        ],
        []
    );

    return (
        <div className="news-editor">
            <Toolbar />
            <ReactQuill
                ref={quillRef}
                theme="snow"
                defaultValue={initialHtml}
                onChange={(html) => onChange && onChange(html)}
                readOnly={readOnly}
                placeholder={placeholder}
                modules={modules}
                formats={formats}
            />
            <style>{editorCss(minLines)}</style>
        </div>
    );
}

/* ---------- Toolbar (no image button) ---------- */
function Toolbar() {
    return (
        <div id="news-toolbar" className="ql-toolbar ql-snow">
            {/* Undo / Redo */}
            <span className="ql-formats">
        <button className="ql-undo" title="Undo"></button>
        <button className="ql-redo" title="Redo"></button>
      </span>

            {/* Headings / paragraph */}
            <span className="ql-formats">
        <select className="ql-header" defaultValue="">
          <option value=""></option>
          <option value="1">H1</option>
          <option value="2">H2</option>
          <option value="3">H3</option>
        </select>
      </span>

            {/* Inline */}
            <span className="ql-formats">
        <button className="ql-bold"></button>
        <button className="ql-italic"></button>
        <button className="ql-underline"></button>
        <select className="ql-background"></select>
      </span>

            {/* Lists + clear + link */}
            <span className="ql-formats">
        <button className="ql-list" value="bullet"></button>
        <button className="ql-list" value="ordered"></button>
        <button className="ql-clean" title="Clear formatting"></button>
        <button className="ql-link"></button>
      </span>
        </div>
    );
}

/* ---------- Helpers ---------- */
function editorCss(minLines) {
    // Minimum height ≈ minLines
    return `
.news-editor .ql-toolbar { border-top-left-radius: 6px; border-top-right-radius: 6px; }
.news-editor .ql-container { border-bottom-left-radius: 6px; border-bottom-right-radius: 6px; font-size: 16px; }
.news-editor .ql-editor { min-height: calc(1.4em * ${Math.max(1, Number(minLines) || 15)}); padding-bottom: 1.5rem; }

/* Icons for custom buttons */
.ql-undo::before { content: '↶'; }
.ql-redo::before { content: '↷'; }
`;
}
